import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { 
  X, ArrowRight, ArrowLeft, Check, Sparkles, Play,
  LayoutDashboard, MessageSquare, Calendar, FolderKanban,
  Wallet, FileText, Settings, Users, HandCoins, Target,
  Clock, BookOpen, Bell, FileSignature, Briefcase, Star
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';

// Icon mapping for dynamic icons
const ICON_MAP = {
  LayoutDashboard, MessageSquare, Calendar, FolderKanban,
  Wallet, FileText, Settings, Users, HandCoins, Target,
  Clock, BookOpen, Bell, FileSignature, Briefcase, Star, Sparkles
};

// Default tour steps per role (used as fallback if no custom config)
const DEFAULT_TOUR_STEPS = {
  client: [
    {
      icon: 'LayoutDashboard',
      title: "Votre Tableau de Bord",
      description: "Bienvenue ! Ici vous voyez en un coup d'œil vos projets en cours, votre solde d'heures et les dernières actualités de l'agence.",
      highlight: "dashboard",
      videoUrl: null
    },
    {
      icon: 'FolderKanban',
      title: "Vos Projets",
      description: "Dans cette section, consultez tous vos projets. Vous pouvez voir l'avancement, valider les livrables et échanger avec l'équipe créative.",
      highlight: "projects",
      videoUrl: null
    },
    {
      icon: 'MessageSquare',
      title: "Messagerie",
      description: "Communiquez facilement avec l'agence. Posez vos questions, envoyez des fichiers et suivez toutes vos conversations en un seul endroit.",
      highlight: "chat",
      videoUrl: null
    },
    {
      icon: 'Wallet',
      title: "Votre Portefeuille",
      description: "Gérez votre crédit d'heures. Rechargez votre solde, consultez vos consommations et téléchargez vos factures.",
      highlight: "wallet",
      videoUrl: null
    },
    {
      icon: 'Calendar',
      title: "Rendez-vous",
      description: "Planifiez et gérez vos rendez-vous avec l'agence. Acceptez les propositions ou demandez un autre créneau.",
      highlight: "calendar",
      videoUrl: null
    },
    {
      icon: 'BookOpen',
      title: "Ressources & Tutoriels",
      description: "Accédez aux guides, tutoriels et ressources partagées par l'agence pour vous accompagner.",
      highlight: "knowledge",
      videoUrl: null
    },
    {
      icon: 'Bell',
      title: "Notifications",
      description: "Restez informé ! Les notifications vous alertent des nouveaux livrables, messages et mises à jour importantes.",
      highlight: "notifications",
      videoUrl: null
    }
  ],
  team_member: [
    {
      icon: 'LayoutDashboard',
      title: "Votre Espace de Travail",
      description: "Bienvenue ! Votre tableau de bord affiche vos tâches du jour, vos heures travaillées et les projets en cours.",
      highlight: "dashboard",
      videoUrl: null
    },
    {
      icon: 'Calendar',
      title: "Votre Planning",
      description: "Consultez votre emploi du temps, vos rendez-vous et les créneaux de travail prévus.",
      highlight: "planning",
      videoUrl: null
    },
    {
      icon: 'FolderKanban',
      title: "Vos Projets Assignés",
      description: "Accédez aux projets sur lesquels vous travaillez. Mettez à jour vos tâches et consultez les briefs clients.",
      highlight: "projects",
      videoUrl: null
    },
    {
      icon: 'Clock',
      title: "Suivi du Temps",
      description: "Lancez le chronomètre pour tracker vos heures de travail. Vos temps sont automatiquement enregistrés.",
      highlight: "timer",
      videoUrl: null
    },
    {
      icon: 'FileText',
      title: "Rapports Mensuels",
      description: "Chaque mois, déclarez vos heures travaillées. Téléversez vos factures depuis cette section.",
      highlight: "reports",
      videoUrl: null
    },
    {
      icon: 'FileSignature',
      title: "Vos Contrats",
      description: "Signez votre contrat mensuel pour débloquer votre accès. Un nouveau contrat est requis chaque mois.",
      highlight: "contracts",
      videoUrl: null
    },
    {
      icon: 'MessageSquare',
      title: "Messagerie",
      description: "Échangez avec l'agence et vos collègues. Toutes les conversations projet sont accessibles ici.",
      highlight: "chat",
      videoUrl: null
    }
  ],
  affiliate: [
    {
      icon: 'Sparkles',
      title: "Bienvenue Apporteur d'Affaires !",
      description: "Félicitations ! Vous faites maintenant partie de notre réseau de partenaires. Recommandez des clients et gagnez des commissions.",
      highlight: "dashboard",
      videoUrl: null
    },
    {
      icon: 'Target',
      title: "Ajouter des Leads",
      description: "Saisissez les coordonnées des prospects que vous nous recommandez. Plus ils sont qualifiés, plus vos chances de conversion sont élevées.",
      highlight: "leads",
      videoUrl: null
    },
    {
      icon: 'HandCoins',
      title: "Vos Commissions",
      description: "Suivez vos gains ! Vous touchez 10% du premier projet payé par chaque client que vous apportez.",
      highlight: "commissions",
      videoUrl: null
    },
    {
      icon: 'Users',
      title: "Lien de Parrainage",
      description: "Partagez votre lien unique ! Tous les prospects qui s'inscrivent via ce lien vous sont automatiquement attribués.",
      highlight: "referral",
      videoUrl: null
    },
    {
      icon: 'FileSignature',
      title: "Votre Contrat",
      description: "Consultez les conditions de votre partenariat et les modalités de commissionnement.",
      highlight: "contract",
      videoUrl: null
    }
  ],
  agency: [
    {
      icon: 'LayoutDashboard',
      title: "Tableau de Bord Agence",
      description: "Bienvenue ! Votre centre de commande pour gérer l'ensemble de votre agence : clients, équipe, finances et projets.",
      highlight: "dashboard",
      videoUrl: null
    },
    {
      icon: 'Calendar',
      title: "Agenda",
      description: "Gérez votre calendrier, les rendez-vous clients et les créneaux de disponibilité de votre équipe.",
      highlight: "calendar",
      videoUrl: null
    },
    {
      icon: 'Users',
      title: "Gestion Clients",
      description: "Consultez et gérez tous vos clients, leurs projets et leurs informations de contact.",
      highlight: "clients",
      videoUrl: null
    },
    {
      icon: 'Briefcase',
      title: "Équipe",
      description: "Administrez les membres de votre équipe : contrats, plannings, évaluations et suivi des temps.",
      highlight: "team",
      videoUrl: null
    },
    {
      icon: 'Wallet',
      title: "Finances",
      description: "Suivez la facturation, les paiements, le chiffre d'affaires et les indicateurs financiers de l'agence.",
      highlight: "finances",
      videoUrl: null
    },
    {
      icon: 'Settings',
      title: "Paramètres",
      description: "Personnalisez votre agence : logo, couleurs, informations légales et configuration des visites guidées.",
      highlight: "settings",
      videoUrl: null
    }
  ]
};

const GuidedTour = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [showTour, setShowTour] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tourConfig, setTourConfig] = useState(null);
  const [steps, setSteps] = useState([]);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [tourEnabled, setTourEnabled] = useState(true);

  // Determine tour key based on user role
  const getTourKey = useCallback(() => {
    if (!user) return null;
    
    if (user.role === 'client') return 'client';
    if (user.team_member_type === 'affiliate') return 'affiliate';
    if (user.role === 'team_member') return 'team_member';
    
    // Agency roles also get a guided tour
    if (['agency', 'project_manager', 'customer_service'].includes(user.role)) return 'agency';
    
    return null;
  }, [user]);

  const tourKey = getTourKey();

  // Fetch tour configuration and user preferences
  useEffect(() => {
    if (!user?.id || !tourKey) {
      setLoading(false);
      return;
    }

    const fetchTourData = async () => {
      try {
        // Fetch tour settings
        const [settingsRes, prefsRes] = await Promise.all([
          api.get('/settings/guided-tour').catch(() => ({ data: null })),
          api.get('/users/preferences').catch(() => ({ data: {} }))
        ]);

        const settings = settingsRes.data;
        const prefs = prefsRes.data || {};

        // Check if tour is globally enabled
        if (settings && settings.enabled === false) {
          setTourEnabled(false);
          setLoading(false);
          return;
        }

        // Check if user has completed/skipped the tour
        if (prefs.guided_tour_completed || prefs.guided_tour_skipped) {
          setLoading(false);
          return;
        }
        
        // IMPORTANT: Don't show guided tour if onboarding not completed
        // Onboarding modal has priority over guided tour
        if (!user.onboarding_completed && user.role !== 'agency' && user.role !== 'client') {
          setLoading(false);
          return;
        }

        // Get custom steps or use defaults
        const customSteps = settings?.tours?.[tourKey];
        const tourSteps = customSteps && customSteps.length > 0 
          ? customSteps 
          : DEFAULT_TOUR_STEPS[tourKey] || [];

        setTourConfig(settings);
        setSteps(tourSteps);
        
        if (tourSteps.length > 0) {
          setShowTour(true);
        }
      } catch (error) {
        console.error('Error fetching tour data:', error);
        // Use defaults on error
        const defaultSteps = DEFAULT_TOUR_STEPS[tourKey] || [];
        setSteps(defaultSteps);
        if (defaultSteps.length > 0) {
          setShowTour(true);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTourData();
  }, [user, tourKey]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeTour = async () => {
    try {
      await api.put('/users/preferences', { 
        guided_tour_completed: true,
        guided_tour_completed_at: new Date().toISOString(),
        guided_tour_skipped: dontShowAgain
      });
      if (!dontShowAgain) {
        toast.success('Visite terminée ! La visite interactive va maintenant commencer.');
      }
      setShowTour(false);
      // Dispatch event to trigger interactive tour
      window.dispatchEvent(new CustomEvent('guided-tour-completed'));
    } catch (error) {
      console.error('Error saving tour completion:', error);
      setShowTour(false);
    }
  };

  const skipTour = async () => {
    try {
      await api.put('/users/preferences', { 
        guided_tour_completed: dontShowAgain,
        guided_tour_skipped: dontShowAgain
      });
    } catch (error) {
      console.error('Error skipping tour:', error);
    }
    setShowTour(false);
  };

  if (loading || !showTour || steps.length === 0 || !tourEnabled) {
    return null;
  }

  const step = steps[currentStep];
  const IconComponent = ICON_MAP[step.icon] || Sparkles;
  const isLastStep = currentStep === steps.length - 1;
  const hasVideo = step.videoUrl && step.videoUrl.trim() !== '';

  // Extract YouTube video ID
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };

  const videoId = getYouTubeId(step.videoUrl);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-300">
      <Card className="w-[95%] max-w-lg mx-4 shadow-2xl border-0 overflow-hidden">
        {/* Progress bar */}
        <div className="h-1.5 bg-slate-100">
          <div 
            className="h-full transition-all duration-500 ease-out"
            style={{ 
              width: `${((currentStep + 1) / steps.length) * 100}%`,
              backgroundColor: theme.colors?.accent || '#3b82f6'
            }}
          />
        </div>
        
        <CardContent className="p-0">
          {/* Video section */}
          {hasVideo && videoId && (
            <div className="relative w-full aspect-video bg-black">
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
                title={step.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}

          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <Badge 
                className="text-xs font-medium"
                style={{ backgroundColor: `${theme.colors?.accent}15`, color: theme.colors?.accent }}
              >
                Étape {currentStep + 1} sur {steps.length}
              </Badge>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 rounded-full hover:bg-slate-100"
                onClick={skipTour}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Icon (only if no video) */}
            {!hasVideo && (
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
                style={{ 
                  background: `linear-gradient(135deg, ${theme.colors?.accent}20, ${theme.colors?.accent}40)`
                }}
              >
                <IconComponent className="w-8 h-8" style={{ color: theme.colors?.accent }} />
              </div>
            )}

            {/* Content */}
            <div className="text-center mb-6">
              <h2 
                className="text-xl font-bold mb-3"
                style={{ color: theme.colors?.primary }}
              >
                {step.title}
              </h2>
              <p className="text-slate-600 text-sm leading-relaxed">
                {step.description}
              </p>
            </div>

            {/* Don't show again checkbox */}
            <div className="flex items-center justify-center gap-2 mb-4 p-3 bg-slate-50 rounded-lg">
              <Checkbox 
                id="dontShowAgain"
                checked={dontShowAgain}
                onCheckedChange={setDontShowAgain}
              />
              <label 
                htmlFor="dontShowAgain" 
                className="text-sm text-slate-600 cursor-pointer select-none"
              >
                Ne plus afficher cette visite guidée
              </label>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between gap-3">
              <Button
                variant="outline"
                onClick={handlePrev}
                disabled={currentStep === 0}
                className="flex-1"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Précédent
              </Button>
              
              <Button
                onClick={handleNext}
                className="flex-1 text-white"
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {isLastStep ? (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    Terminer
                  </>
                ) : (
                  <>
                    Suivant
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>

            {/* Step indicators */}
            <div className="flex items-center justify-center gap-1.5 mt-4">
              {steps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={cn(
                    "w-2 h-2 rounded-full transition-all duration-300",
                    index === currentStep 
                      ? "w-6" 
                      : "hover:opacity-70"
                  )}
                  style={{ 
                    backgroundColor: index === currentStep 
                      ? theme.colors?.accent 
                      : index < currentStep 
                        ? `${theme.colors?.accent}60`
                        : '#e2e8f0'
                  }}
                />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GuidedTour;
